import React from 'react';
import { BzImage, buildCloudflareImageUrl } from '@benzinga/image';
import { Button } from '@benzinga/core-ui';
import styled from '@benzinga/themetron';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { formatDatePretty } from '@benzinga/date-utils';
import striptags from 'striptags';
import { convertToRelativeUrl } from '@benzinga/utils';

export interface FeaturedPodcastCardProps {
  episode: FeaturedPodcastCardEpisode;
}

export interface FeaturedPodcastCardEpisode {
  id: number;
  title: string;
  description: string;
  date: string;
  link: string;
  image: string;
  custom_description?: string | null;
  custom_title?: string | null;
  show_name?: string | null;
  theme_color?: string;
}

export const FeaturedPodcastCard: React.FC<FeaturedPodcastCardProps> = ({ episode }) => {
  const shadedColor = shadeColor(episode.theme_color || '#34c7f3', -50);
  const linearGradient = `linear-gradient(347deg, ${shadedColor} 1%, ${episode.theme_color || '#34c7f3'} 100%)`;

  const date = episode.date ? formatDatePretty(episode.date, { timePeriod: { month: 'short' } }) : '-';

  return (
    <Container
      $backgroundColor={shadedColor}
      $linearGradient={linearGradient}
      className="flex flex-col items-center text-center sm:text-left sm:items-[unset] sm:flex-row min-h-[250px] h-full p-4 rounded-t-md"
      href={convertToRelativeUrl(episode.link || '')}
    >
      <div className="flex items-center justify-center max-w-[215px] max-h-[215px] mb-4 sm:mb-0 sm:mr-8 relative rounded overflow-hidden w-full w-[215px] h-[215px] min-w-[215px]">
        <BzImage
          alt=""
          blurDataURL={buildCloudflareImageUrl(episode.image, 'width=10,dpr=1,format=auto,quality=85')}
          blurOpacity={10}
          layout="fill"
          loaderProps={{ width: 220 }}
          loading="lazy"
          objectFit="cover"
          placeholder="blur"
          preload={true}
          sizes="(max-width: 800px) 320px, (min-width: 800px) 320px"
          src={episode.image}
        />
      </div>
      <div>
        <Title
          className="text-2xl text-white uppercase mb-2 sm:max-w-[85%]"
          dangerouslySetInnerHTML={{ __html: sanitizeHTML(episode.custom_title || episode.title) }}
        />
        <Subtitle
          className="featured-podcast-card-description text-white text-base font-light sm:max-w-[85%]"
          dangerouslySetInnerHTML={{ __html: sanitizeHTML(striptags(episode.description)) }}
        />
        <div className="flex flex-col sm:flex-row gap-4 items-center text-white md:whitespace-nowrap">
          <Button className="uppercase w-full sm:w-[100px]" variant="white-outline">
            Listen
          </Button>
          <div className="flex flex-wrap gap-x-2 justify-center">
            <time dateTime={date}>{date}</time>
            <span className="hidden sm:block">·</span>
            {episode?.show_name && (
              <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(striptags(episode?.show_name)) }} />
            )}
          </div>
        </div>
      </div>
    </Container>
  );
};

export default FeaturedPodcastCard;

const shadeColor = (color: string, percent: number): string => {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = (R * (100 + percent)) / 100;
  G = (G * (100 + percent)) / 100;
  B = (B * (100 + percent)) / 100;

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  R = Math.round(R);
  G = Math.round(G);
  B = Math.round(B);

  const RR = R.toString(16).length === 1 ? '0' + R.toString(16) : R.toString(16);
  const GG = G.toString(16).length === 1 ? '0' + G.toString(16) : G.toString(16);
  const BB = B.toString(16).length === 1 ? '0' + B.toString(16) : B.toString(16);

  return '#' + RR + GG + BB;
};

const Container = styled.a<{ $backgroundColor: string; $linearGradient: string }>`
  background: ${({ $backgroundColor }) => $backgroundColor || '#34c7f3'};
  background: ${({ $linearGradient }) => $linearGradient || 'linear-gradient(347deg,#1a647a 1%,#34c7f3 100%)'};
`;

const Title = styled.h5`
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

const Subtitle = styled.p`
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;

  a {
    color: inherit;
  }
`;
